import latinExt400 from '@fontsource/nunito/files/nunito-latin-ext-400-normal.woff2';
import latinExt500 from '@fontsource/nunito/files/nunito-latin-ext-500-normal.woff2';
import latinExt600 from '@fontsource/nunito/files/nunito-latin-ext-600-normal.woff2';
import latinExt700 from '@fontsource/nunito/files/nunito-latin-ext-700-normal.woff2';
import latinExt800 from '@fontsource/nunito/files/nunito-latin-ext-800-normal.woff2';
import latin400 from '@fontsource/nunito/files/nunito-latin-400-normal.woff2';
import latin500 from '@fontsource/nunito/files/nunito-latin-500-normal.woff2';
import latin600 from '@fontsource/nunito/files/nunito-latin-600-normal.woff2';
import latin700 from '@fontsource/nunito/files/nunito-latin-700-normal.woff2';
import latin800 from '@fontsource/nunito/files/nunito-latin-800-normal.woff2';

/**
 * Police de marque, embarquee.
 *
 * Nunito (ADR 0023) vient de `@fontsource/nunito` : les fichiers `woff2` sont
 * empaquetes par Vite dans `assets/`, l'image Docker reste donc autonome et
 * **aucun domaine externe n'est appele** — ni CDN de polices, ni Google Fonts.
 *
 * **Pourquoi l'API `FontFace` et non les feuilles CSS du paquet.** Ses feuilles
 * par sous-ensemble (`latin-400.css`, `latin-ext-400.css`) n'ont pas de
 * `unicode-range` : les importer toutes deux ferait telecharger le latin etendu
 * pour chaque page. Les declarer ici, avec les plages du paquet, donne le bon
 * comportement : le latin (francais compris, « œ » et « € » inclus) est charge
 * d'emblee, le latin etendu seulement si un nom propre en a besoin.
 *
 * **Pourquoi attendre avant le premier rendu.** `font-display: swap` evite un
 * texte invisible, mais pas la coupure visible du passage police systeme ->
 * Nunito, qui deplace toute la page sous les yeux de l'exploitant. Les cinq
 * fichiers latins (~16 ko chacun, meme origine) sont donc charges avant de
 * monter l'application, **dans la limite d'un delai** : un reseau lent ou une
 * police refusee ne bloquent jamais le demarrage, le texte retombe alors sur la
 * pile systeme declaree par le theme.
 */

/** Plages du paquet (`@fontsource/nunito/400.css`), identiques pour chaque graisse. */
const LATIN_RANGE =
  'U+0000-00FF,U+0131,U+0152-0153,U+02BB-02BC,U+02C6,U+02DA,U+02DC,U+0304,U+0308,U+0329,U+2000-206F,U+20AC,U+2122,U+2191,U+2193,U+2212,U+2215,U+FEFF,U+FFFD';
const LATIN_EXT_RANGE =
  'U+0100-02BA,U+02BD-02C5,U+02C7-02CC,U+02CE-02D7,U+02DD-02FF,U+0304,U+0308,U+0329,U+1D00-1DBF,U+1E00-1E9F,U+1EF2-1EFF,U+2020,U+20A0-20AB,U+20AD-20C0,U+2113,U+2C60-2C7F,U+A720-A7FF';

export interface BrandFontSource {
  readonly weight: '400' | '500' | '600' | '700' | '800';
  readonly url: string;
  readonly unicodeRange: string;
  /** Charge avant le premier rendu (latin) ou a la demande (latin etendu). */
  readonly eager: boolean;
}

const latin = (weight: BrandFontSource['weight'], url: string): BrandFontSource => ({
  weight,
  url,
  unicodeRange: LATIN_RANGE,
  eager: true,
});

const latinExt = (weight: BrandFontSource['weight'], url: string): BrandFontSource => ({
  weight,
  url,
  unicodeRange: LATIN_EXT_RANGE,
  eager: false,
});

export const BRAND_FONT_SOURCES: readonly BrandFontSource[] = [
  latin('400', latin400),
  latin('500', latin500),
  latin('600', latin600),
  latin('700', latin700),
  latin('800', latin800),
  latinExt('400', latinExt400),
  latinExt('500', latinExt500),
  latinExt('600', latinExt600),
  latinExt('700', latinExt700),
  latinExt('800', latinExt800),
];

/** Delai au-dela duquel l'application demarre sans attendre la police. */
export const BRAND_FONT_TIMEOUT_MS = 2000;

export interface BrandFontEnvironment {
  readonly fonts: Pick<FontFaceSet, 'add'>;
  readonly createFace: (family: string, source: string, descriptors: FontFaceDescriptors) => FontFace;
  readonly timeoutMs?: number;
}

/**
 * Declare les dix faces et attend les cinq latines, au plus `timeoutMs`.
 *
 * Ne rejette jamais : une police est un confort, pas une condition de
 * fonctionnement.
 */
export const registerBrandFonts = async ({
  fonts,
  createFace,
  timeoutMs = BRAND_FONT_TIMEOUT_MS,
}: BrandFontEnvironment): Promise<void> => {
  const eagerLoads: Promise<unknown>[] = [];

  for (const source of BRAND_FONT_SOURCES) {
    const face = createFace('Nunito', `url(${source.url}) format('woff2')`, {
      weight: source.weight,
      style: 'normal',
      display: 'swap',
      unicodeRange: source.unicodeRange,
    });

    fonts.add(face);

    if (source.eager) {
      eagerLoads.push(face.load());
    }
  }

  let timer: ReturnType<typeof setTimeout> | undefined;

  await Promise.race([
    Promise.allSettled(eagerLoads),
    new Promise<void>((resolve) => {
      timer = setTimeout(resolve, timeoutMs);
    }),
  ]);

  clearTimeout(timer);
};

/** Raccord au navigateur reel ; absent (environnement sans `FontFace`), on n'attend rien. */
export const loadBrandFonts = (): Promise<void> =>
  typeof FontFace === 'undefined'
    ? Promise.resolve()
    : registerBrandFonts({
        fonts: document.fonts,
        createFace: (family, source, descriptors) => new FontFace(family, source, descriptors),
      });
